import React, { useState, useEffect } from 'react'
import Alert from '~/Common-Objects/UI/Alert'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import CheckBox from '~/Common-Objects/UI/Forms/CheckBox/CheckBox'
import FormError from '~/Common-Objects/UI/Forms/FormError/FormError'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'

export const ANALYTICS_PROVIDERS = [
    { value: 'ga', label: 'Google Analytics', optionKey: 'gaTag', placeholder: 'G-XXXXXXXX' },
    { value: 'clarity', label: 'Microsoft Clarity', optionKey: 'clarityId', placeholder: 'Project ID' },
    { value: 'hotjar', label: 'Hotjar', optionKey: 'hotjarId', placeholder: 'Site ID' },
    { value: 'meta', label: 'Meta Pixel', optionKey: 'metaPixelId', placeholder: 'Pixel ID' },
    {
        value: 'linkedin',
        label: 'LinkedIn Insight',
        optionKey: 'linkedinPartnerId',
        placeholder: 'Partner ID'
    },
    { value: 'tiktok', label: 'TikTok Pixel', optionKey: 'tiktokPixelId', placeholder: 'Pixel ID' }
]

export function getSelectedAnalyticsProviders(options = {}) {
    if (Array.isArray(options.analyticsProviders)) return options.analyticsProviders
    if (options.gaTag) return ['ga']
    return []
}

export function validateAnalyticsOptions(options = {}) {
    const errs = {}
    const selected = getSelectedAnalyticsProviders(options)
    for (const key of selected) {
        const provider = ANALYTICS_PROVIDERS.find((p) => p.value === key)
        if (!provider) continue
        const raw = options[provider.optionKey]
        const value = raw == null ? '' : String(raw).trim()
        if (!value) {
            errs[provider.optionKey] = `${provider.label} ID is required`
            continue
        }
        if (key === 'ga') {
            if (/^UA-/i.test(value)) {
                errs[provider.optionKey] =
                    'Universal Analytics (UA-) is not supported. Use a GA4 Measurement ID (G-...).'
            } else if (!/^(G-)?[A-Z0-9]+$/i.test(value)) {
                errs[provider.optionKey] = 'Enter a valid GA4 Measurement ID (G-XXXXXXXX)'
            }
        } else if (key === 'clarity' || key === 'tiktok') {
            if (!/^[A-Z0-9]+$/i.test(value)) errs[provider.optionKey] = `Enter a valid ${provider.label} ID`
        } else if (!/^\d+$/.test(value)) {
            errs[provider.optionKey] = `Enter a valid ${provider.label} ID`
        }
    }
    return errs
}

export default function LandingPageSettingAnalytics({
    landingPage,
    setValue,
    setOptions,
    saveLandingPage = () => {},
    errors = {}
}) {
    const [disabled, setDisabled] = useState(!landingPage.options?.trackingDomain)

    useEffect(() => {
        setDisabled(!landingPage.options?.trackingDomain)
    }, [landingPage.options?.trackingDomain])

    const selectedProviders = getSelectedAnalyticsProviders(landingPage.options || {})

    const onProvidersChange = (ev) => {
        const value = ev.currentTarget?.value ?? ev.target?.value
        const next = [...selectedProviders]
        const idx = next.indexOf(value)
        if (idx > -1) next.splice(idx, 1)
        else next.push(value)

        const options = { ...(landingPage.options || {}), analyticsProviders: next }
        for (const provider of ANALYTICS_PROVIDERS) {
            if (!next.includes(provider.value)) options[provider.optionKey] = ''
        }
        setValue('options', options)
    }

    return (
        <Paper className='paper-padding-24' style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {disabled && (
                <div style={{ position: 'absolute', top: 10, right: 10 }}>
                    <Alert
                        className='tight'
                        type='info'
                        hideIcon
                        topContent={
                            <div className='text-heavy'>
                                Analytics settings are only available when using custom domains
                            </div>
                        }
                    ></Alert>
                </div>
            )}
            <h4>Analytics</h4>
            <Select
                label='Analytics providers'
                labelSub='Choose which analytics tools to add to this landing page.'
                multiple={true}
                data={ANALYTICS_PROVIDERS}
                value={disabled ? [] : selectedProviders}
                disabled={disabled}
                placeholder={disabled ? 'Analytics providers are not available' : 'Select providers'}
                onChange={onProvidersChange}
            />

            {!disabled &&
                ANALYTICS_PROVIDERS.filter((p) => selectedProviders.includes(p.value)).map((provider) => (
                    <div key={provider.value}>
                        <TextInput
                            label={provider.label}
                            labelSub={
                                provider.value === 'ga'
                                    ? 'Enter your GA4 Measurement ID (G-XXXXXXXX). Universal Analytics (UA-) is not supported.'
                                    : `Enter your ${provider.label} ID to enable on this landing page.`
                            }
                            value={landingPage.options?.[provider.optionKey] || ''}
                            placeholder={provider.placeholder}
                            disabled={disabled}
                            onChange={(e) => setOptions(provider.optionKey, e.target.value)}
                        />
                        {!!errors?.[provider.optionKey] && (
                            <FormError>{errors[provider.optionKey]}</FormError>
                        )}
                    </div>
                ))}

            <CheckBox
                checked={!disabled && landingPage?.options.allowIndexing}
                onChange={(e) => setOptions('allowIndexing', e.target.checked)}
                disabled={disabled}
            >
                <div className='txtInput_label_sub'>
                    <div className='txtInput_label'>Allow site indexing</div>
                    <div className='txtInput_labelSub'>
                        If disabled, your page will not appear on search engine results and search engines will be asked
                        not to crawl this page.
                    </div>
                </div>
            </CheckBox>

            <div>
                <Button className='button-primary' onClick={() => saveLandingPage()} disabled={disabled}>
                    Save
                </Button>
            </div>
        </Paper>
    )
}
